/**
 * Entitlements API Contract (plan #004, U7 — non-gating resolver).
 *
 * The resolver `isEntitled(schoolId, userId, feature)` derives from real
 * subscription/grant data and returns a machine-readable, SELF-DECLARING
 * shape: `{ granted, enforcing: false }`. `enforcing` is a literal `false`
 * this phase (KTD-6) so no caller can mistake the advisory result for a gate.
 * Promotion to enforcing is a deliberate, separate API surface later — never
 * a flag flip on this contract.
 *
 * The admin resolve-endpoint (super-admin gated) exercises the resolver
 * against live data:
 *  - GET /v1/schools/:school_id/resolve-entitlements?user_id=&feature=
 */

import { z } from 'zod'

/**
 * The resolver's per-feature result. `enforcing` is a literal `false` this
 * phase (KTD-6) — advisory only, gates nothing. A contract test asserts the
 * resolver never returns `enforcing: true` before the enforcement phase.
 */
export const EntitlementResultSchema = z.object({
  granted: z.boolean().describe('Whether the feature is entitled by the derived grant set'),
  enforcing: z.literal(false).describe('Always false this phase — the result is advisory, not a gate (KTD-6)')
})

export type EntitlementResult = z.infer<typeof EntitlementResultSchema>

/**
 * Request for the admin resolve-endpoint. `feature` (a `Component.slug`) is
 * optional: when present, resolve that single feature; when absent, resolve
 * the full entitlement map for the school+user.
 */
export const ResolveEntitlementsRequestSchema = z.object({
  school_public_id: z.string().describe('School unique identifier'),
  user_id: z.string().optional().describe('Resolve as this user (USER-owned grants); omitted = school-only'),
  feature: z.string().optional().describe('A single Component.slug to resolve; omitted = resolve all features')
})

export type ResolveEntitlementsRequest = z.infer<typeof ResolveEntitlementsRequestSchema>

/**
 * A single resolved feature row: the feature slug + its result.
 */
export const ResolvedFeatureSchema = z.object({
  feature: z.string().describe('The Component.slug'),
  result: EntitlementResultSchema
})

export type ResolvedFeature = z.infer<typeof ResolvedFeatureSchema>

/**
 * Response for the admin resolve-endpoint: the resolved entitlement set for
 * the requested school+user. `entitlements` is the full map (or the single
 * requested feature). `enforcing` echoes the phase-wide false for clarity.
 */
export const ResolveEntitlementsResponseSchema = z.object({
  schoolId: z.string().describe('School public ID the resolution was run for'),
  userId: z.string().nullable().describe('User public ID the resolution was run for, or null for school-only'),
  enforcing: z.literal(false).describe('Always false this phase — the resolver is non-gating (KTD-6)'),
  entitlements: z.array(ResolvedFeatureSchema).describe('Resolved per-feature results')
})

export type ResolveEntitlementsResponse = z.infer<typeof ResolveEntitlementsResponseSchema>
